import React from "react";

import { dataRoomHandoffContent } from "../content/dataRoomHandoff";
import type { Locale, WorkflowContent } from "../content/types";

type DataRoomsWorkflowProps = {
  content: WorkflowContent;
  locale: Locale;
};

export function DataRoomsWorkflow({ content, locale }: DataRoomsWorkflowProps) {
  const handoff = dataRoomHandoffContent;
  const workspacePropertiesLabel = locale === "sv" ? "Egenskaper för arbetsytan" : "Workspace properties";

  return (
    <section className="db-section section-band db-data-rooms-section" id={content.id} aria-labelledby={`${content.id}-title`}>
      <div className="db-container db-section-container">
        <header className="section-heading db-section-heading db-data-rooms-heading">
          <p className="section-eyebrow db-eyebrow">{content.eyebrow}</p>
          <h2 id={`${content.id}-title`}>{content.title}</h2>
          <p className="db-section-intro">{content.lead}</p>
          <p className="db-section-boundary">{content.boundary ?? handoff.boundary[locale]}</p>
          <p className="db-room-relationship">{handoff.relationship[locale]}</p>
        </header>

        <div className="db-room-illustration" role="img" aria-label={handoff.illustration.ariaLabel[locale]}>
          <div className="db-room-sources">
            <div className="db-room-sources__heading">
              <span className="db-node-kicker">{handoff.illustration.sources.kicker[locale]}</span>
              <strong>{handoff.illustration.sources.title[locale]}</strong>
            </div>

            <div className="db-room-source-list">
              {handoff.illustration.sources.items[locale].map((item) => (
                <span key={item}>{item}</span>
              ))}
            </div>
          </div>

          <FlowLine />

          <div className="db-room-workspace">
            <div className="db-room-workspace__topbar">
              <div>
                <span className="db-node-kicker">{handoff.illustration.workspace.kicker[locale]}</span>
                <h3>{handoff.illustration.workspace.title[locale]}</h3>
              </div>

              <div className="db-room-statuses" aria-label={workspacePropertiesLabel}>
                {handoff.illustration.workspace.statuses[locale].map((status) => (
                  <span className="db-status-chip" key={status}>
                    {status}
                  </span>
                ))}
              </div>
            </div>

            <div className="db-room-workspace__body">
              {handoff.illustration.workspace.panels.map((panel) => (
                <article
                  className={`db-room-panel${panel.id === "agent" ? " db-room-panel--agent" : ""}`}
                  data-room-panel={panel.id}
                  key={panel.id}
                >
                  <div className="db-room-panel__heading">
                    <span className="db-panel-icon" aria-hidden="true">
                      {panel.iconLabel[locale]}
                    </span>

                    <div>
                      <span className="db-node-kicker">{panel.kicker[locale]}</span>
                      <h4>{panel.title[locale]}</h4>
                    </div>
                  </div>

                  <ul className="db-room-panel__list">
                    {panel.items[locale].map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>

                  {panel.footer ? (
                    <div className="db-room-panel__footer">
                      <span>{panel.footer[locale]}</span>
                    </div>
                  ) : null}

                  {panel.capacity ? (
                    <div className="db-agent-capacity">
                      <div className="db-agent-capacity__heading">
                        <span>{panel.capacity.label[locale]}</span>
                        <strong>{panel.capacity.title[locale]}</strong>
                      </div>

                      <div className="db-agent-capacity__meter" aria-hidden="true">
                        <span />
                      </div>

                      <p>{panel.capacity.body[locale]}</p>
                    </div>
                  ) : null}

                  {panel.review ? (
                    <div className="db-room-review-status">
                      <span className="db-review-panel__status">{panel.review.status[locale]}</span>
                      <small>{panel.review.body[locale]}</small>
                    </div>
                  ) : null}
                </article>
              ))}
            </div>

            <div className="db-room-evidence-trail">
              <div className="db-room-evidence-trail__heading">
                <span className="db-node-kicker">{handoff.illustration.workspace.evidenceTrail.kicker[locale]}</span>
                <strong>{handoff.illustration.workspace.evidenceTrail.title[locale]}</strong>
              </div>

              <div className="db-room-evidence-items">
                {handoff.illustration.workspace.evidenceTrail.items[locale].map((item) => (
                  <span key={item}>{item}</span>
                ))}
              </div>
            </div>
          </div>

          <FlowLine />

          <div className="db-room-output">
            <div className="db-room-output__heading">
              <span className="db-node-kicker">{handoff.illustration.output.kicker[locale]}</span>
              <strong>{handoff.illustration.output.title[locale]}</strong>
            </div>

            <div className="db-room-output__items">
              {handoff.illustration.output.items.map((item, index) => (
                <div className="db-room-output__item" key={item.title[locale]}>
                  <span aria-hidden="true">{String(index + 1).padStart(2, "0")}</span>
                  <strong>{item.title[locale]}</strong>
                  <small>{item.body[locale]}</small>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="db-room-process">
          <div className="db-subsection-heading">
            <p className="section-eyebrow db-eyebrow">{handoff.process.eyebrow[locale]}</p>
            <h3>{handoff.process.title[locale]}</h3>
          </div>

          <div className="db-room-process__grid">
            {handoff.stages.map((stage, index) => (
              <article
                className="db-process-card"
                data-workflow-card
                data-workflow-card-family="data-rooms"
                data-workflow-stage={stage.id}
                key={stage.id}
              >
                <span className="db-process-card__number" aria-hidden="true">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <h4>{stage.title[locale]}</h4>
                <p>{stage.body[locale]}</p>
              </article>
            ))}
          </div>
        </div>

        <div className="db-room-workstreams">
          <div className="db-subsection-heading">
            <p className="section-eyebrow db-eyebrow">{handoff.workstreams.eyebrow[locale]}</p>
            <h3>{handoff.workstreams.title[locale]}</h3>
            <p>{handoff.workstreams.lead[locale]}</p>
          </div>

          <div className="db-workstream-grid">
            {handoff.modules.map((module, index) => (
              <article
                className="db-workstream-card"
                data-card-family="workstream-modules"
                data-workstream-module={module.key}
                key={module.key}
              >
                <span className="db-workstream-card__index" aria-hidden="true">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <h4>{module.title[locale]}</h4>
                <p>{module.body[locale]}</p>
                <span className="db-workstream-card__tag">{module.tag[locale]}</span>
              </article>
            ))}
          </div>
        </div>

        <div className="db-room-delivery">
          <div className="db-subsection-heading">
            <p className="section-eyebrow db-eyebrow">{handoff.delivery.eyebrow[locale]}</p>
            <h3>{handoff.delivery.title[locale]}</h3>
          </div>

          <div className="db-delivery-grid">
            {handoff.deliveryPaths.map((path, index) => (
              <article
                className="db-delivery-card"
                data-card-family="delivery-paths"
                data-delivery-path={path.key}
                key={path.key}
              >
                <span className="db-delivery-card__number" aria-hidden="true">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <h4>{path.title[locale]}</h4>
                <p>{path.body[locale]}</p>
              </article>
            ))}
          </div>
        </div>

        <aside className="db-control-boundary" aria-labelledby={`${content.id}-control-boundary-title`}>
          <div className="db-control-boundary__icon" aria-hidden="true">
            OK
          </div>

          <div>
            <p className="section-eyebrow db-eyebrow">{handoff.controlBoundary.eyebrow[locale]}</p>
            <h3 id={`${content.id}-control-boundary-title`}>{handoff.controlBoundary.title[locale]}</h3>
            <p>{handoff.controlBoundary.body[locale]}</p>
          </div>
        </aside>
      </div>
    </section>
  );
}

function FlowLine() {
  return (
    <div className="db-flow-line db-flow-line--vertical" aria-hidden="true">
      <span className="db-flow-arrow">v</span>
    </div>
  );
}
